import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Mail, CheckCircle2, XCircle, RefreshCw, Link2, AlertTriangle, Clock, Inbox, Unplug,
  Database, Users, TrendingUp, Loader2, Megaphone, PhoneCall, Activity,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

export default function Integrations() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data, isLoading } = useQuery({
    queryKey: ["gmail-status"],
    queryFn: async () => (await api.get("/integrations/gmail/status")).data,
    refetchInterval: (query) => (query.state.data?.sync?.state === "running" ? 3000 : false),
  });
  const gmailRunning = data?.sync?.state === "running";

  // Notifica al termine della sincronizzazione in background
  const prevGmailState = useRef(null);
  useEffect(() => {
    const state = data?.sync?.state;
    if (prevGmailState.current === "running" && state === "done") {
      toast.success(`Sincronizzazione completata: ${data.sync.imported ?? 0} nuovi lead (${data.sync.scanned ?? 0} email scansionate)`);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } else if (prevGmailState.current === "running" && state === "error") {
      toast.error(data?.sync?.error || "Sincronizzazione fallita");
    }
    prevGmailState.current = state;
  }, [data?.sync?.state, data?.sync, qc]);

  useEffect(() => {
    const g = searchParams.get("gmail");
    if (g === "connected") toast.success("Gmail collegato correttamente");
    else if (g === "error") toast.error("Collegamento Gmail non riuscito");
    else if (g === "not_configured") toast.error("Credenziali Google non configurate sul server");
    if (g) { qc.invalidateQueries({ queryKey: ["gmail-status"] }); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams, qc]);

  const sync = useMutation({
    mutationFn: async () => (await api.post("/integrations/gmail/sync")).data,
    onSuccess: (res) => {
      if (res.started) toast.success("Sincronizzazione Gmail avviata: continuerà in background");
      else toast.info(res.message || "Sincronizzazione già in corso");
      qc.invalidateQueries({ queryKey: ["gmail-status"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sincronizzazione fallita"),
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/integrations/gmail/disconnect")).data,
    onSuccess: () => { toast.success("Gmail scollegato"); qc.invalidateQueries({ queryKey: ["gmail-status"] }); },
  });

  const connectGmail = () => {
    // Browser navigation to backend OAuth start; httpOnly session cookie travels with it.
    window.location.href = `${API}/oauth/gmail/login`;
  };

  // ---- Pipedrive ----
  const { data: pd, isLoading: pdLoading } = useQuery({
    queryKey: ["pipedrive-status"],
    queryFn: async () => (await api.get("/integrations/pipedrive/status")).data,
    refetchInterval: (query) => (query.state.data?.sync?.state === "running" ? 3000 : false),
  });
  const pdRunning = pd?.sync?.state === "running";

  const pdConnect = useMutation({
    mutationFn: async () => (await api.post("/integrations/pipedrive/connect")).data,
    onSuccess: (r) => { toast.success(`Pipedrive collegato: ${r.company}`); qc.invalidateQueries({ queryKey: ["pipedrive-status"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Collegamento Pipedrive fallito"),
  });
  const pdSync = useMutation({
    mutationFn: async () => (await api.post("/integrations/pipedrive/sync")).data,
    onSuccess: (r) => {
      if (r.started) toast.success("Sincronizzazione Pipedrive avviata");
      else toast.info(r.message || "Sincronizzazione già in corso");
      qc.invalidateQueries({ queryKey: ["pipedrive-status"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sincronizzazione fallita"),
  });
  const pdDisconnect = useMutation({
    mutationFn: async () => (await api.post("/integrations/pipedrive/disconnect")).data,
    onSuccess: () => {
      toast.success("Pipedrive scollegato");
      qc.invalidateQueries({ queryKey: ["pipedrive-status"] });
      qc.invalidateQueries({ queryKey: ["analytics-speed"] });
    },
  });

  const { data: pdUsage } = useQuery({
    queryKey: ["pipedrive-usage"],
    queryFn: async () => (await api.get("/integrations/pipedrive/usage")).data,
    enabled: !!pd?.connected,
    refetchInterval: pdRunning ? 5000 : 60000,
  });

  const connected = data?.connected;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }) : "Mai");

  return (
    <div className="max-w-3xl space-y-6" data-testid="integrations-page">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Mail className="h-5 w-5 text-blue-700" /> Gmail (sola lettura)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div className="flex items-center gap-3">
                  {connected ? (
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-slate-400" />
                  )}
                  <div>
                    <p className="font-medium" data-testid="gmail-connection-status">
                      {connected ? "Collegato" : "Non collegato"}
                    </p>
                    <p className="text-sm text-muted-foreground">{data?.account || "Nessun account collegato"}</p>
                  </div>
                </div>
                {connected ? (
                  <Button variant="outline" size="sm" className="gap-2" data-testid="disconnect-gmail-button" onClick={() => disconnect.mutate()}>
                    <Unplug className="h-4 w-4" /> Scollega
                  </Button>
                ) : (
                  <Button className="gap-2" data-testid="connect-gmail-button" onClick={connectGmail} disabled={!data?.configured}>
                    <Link2 className="h-4 w-4" /> Collega Gmail
                  </Button>
                )}
              </div>

              {!data?.configured && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Le credenziali Google OAuth (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET) non sono ancora configurate
                    sul server. Il pulsante sarà attivo una volta inserite nelle variabili d'ambiente.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Stat icon={Clock} label="Ultima sincronizzazione" value={fmt(data?.last_sync)} />
                <Stat icon={Inbox} label="Email importate" value={data?.imported_count ?? 0} />
                <Stat
                  icon={data?.last_sync_status === "error" ? AlertTriangle : CheckCircle2}
                  label="Stato ultima sync"
                  value={data?.last_sync_status ? (data.last_sync_status === "error" ? "Errore" : "OK") : "—"}
                />
              </div>

              {data?.last_error && (
                <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="gmail-error">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>{data.last_error}</span>
                </div>
              )}

              {gmailRunning && (
                <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="gmail-sync-progress">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Sincronizzazione Gmail in corso… puoi continuare a usare l'app, i nuovi lead appariranno al termine.</span>
                </div>
              )}

              <Button
                className="w-full gap-2 sm:w-auto"
                data-testid="sync-now-button"
                onClick={() => sync.mutate()}
                disabled={!connected || sync.isPending || gmailRunning}
              >
                <RefreshCw className={`h-4 w-4 ${sync.isPending || gmailRunning ? "animate-spin" : ""}`} /> Sincronizza ora
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm" data-testid="pipedrive-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Database className="h-5 w-5 text-emerald-700" /> Pipedrive (sola lettura)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {pdLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <>
              <div className="flex items-center justify-between rounded-lg border border-border p-4">
                <div className="flex items-center gap-3">
                  {pd?.connected ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <XCircle className="h-6 w-6 text-slate-400" />}
                  <div>
                    <p className="font-medium" data-testid="pipedrive-connection-status">
                      {pd?.connected ? "Collegato" : "Non collegato"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {pd?.connected ? `${pd?.company || pd?.domain} · ${pd?.user_name || ""}` : "Nessun account collegato"}
                    </p>
                  </div>
                </div>
                {pd?.connected ? (
                  <Button variant="outline" size="sm" className="gap-2" data-testid="disconnect-pipedrive-button" onClick={() => pdDisconnect.mutate()} disabled={pdRunning}>
                    <Unplug className="h-4 w-4" /> Scollega
                  </Button>
                ) : (
                  <Button className="gap-2" data-testid="connect-pipedrive-button" onClick={() => pdConnect.mutate()} disabled={!pd?.configured || pdConnect.isPending}>
                    <Link2 className="h-4 w-4" /> Collega Pipedrive
                  </Button>
                )}
              </div>

              {!pd?.configured && (
                <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>Token/dominio Pipedrive non configurati sul server.</span>
                </div>
              )}

              {pd?.connected && (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Stat icon={Users} label="Lead abbinati" value={pd?.matched_count ?? 0} />
                    <Stat icon={Clock} label="Ultima sincronizzazione" value={fmt(pd?.sync?.finished_at)} />
                    <Stat icon={TrendingUp} label="Stato" value={pd?.sync?.state === "done" ? "Completato" : pd?.sync?.state === "running" ? "In corso" : pd?.sync?.state === "paused" ? "In pausa" : pd?.sync?.state === "error" ? "Errore" : "—"} />
                  </div>

                  {pdRunning && (
                    <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="pipedrive-sync-progress">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>
                        {pd?.sync?.phase || "Sincronizzazione in corso"}…
                        {" "}contatti {pd?.sync?.persons ?? 0} · trattative {pd?.sync?.deals ?? 0} · attività {pd?.sync?.activities ?? 0}
                      </span>
                    </div>
                  )}

                  {pd?.sync?.state === "paused" && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pipedrive-paused">
                      <Clock className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>{pd?.sync?.error || pd?.sync?.phase || "Sincronizzazione in pausa; riprende automaticamente."}</span>
                    </div>
                  )}

                  {pd?.sync?.state === "error" && pd?.sync?.error && (
                    <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="pipedrive-error">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <span>{pd.sync.error}</span>
                    </div>
                  )}

                  <div>
                    <Button className="w-full gap-2 sm:w-auto" data-testid="pipedrive-sync-button" onClick={() => pdSync.mutate()} disabled={pdRunning || pdSync.isPending}>
                      <RefreshCw className={`h-4 w-4 ${pdRunning ? "animate-spin" : ""}`} /> Sincronizza Pipedrive
                    </Button>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Scarica contatti, trattative e attività (sola lettura) e li abbina ai lead per calcolare velocità di risposta, conversioni e vendite reali nell'Analisi Velocità.
                    </p>
                  </div>

                  <PipedriveUsageChart usage={pdUsage} />
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <MetaCard />
      <SalesTrailCard />
    </div>
  );
}

function PipedriveUsageChart({ usage }) {
  const days = usage?.days || [];
  const total = usage?.total_7d ?? 0;
  const today = usage?.today ?? 0;
  const interval = usage?.interval_minutes ?? 10;
  const dailyBudget = usage?.daily_budget ?? 40000;
  const maxTokens = Math.max(1, ...days.map((d) => d.tokens));

  return (
    <div className="rounded-lg border border-border p-4" data-testid="pipedrive-usage-chart">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-emerald-700" />
          <p className="text-sm font-semibold">Consumo token API Pipedrive · ultimi 7 giorni</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span data-testid="pd-usage-today">Oggi: <strong className="text-foreground">{today.toLocaleString("it-IT")}</strong> token</span>
          <span data-testid="pd-usage-total">Totale 7gg: <strong className="text-foreground">{total.toLocaleString("it-IT")}</strong> token</span>
        </div>
      </div>

      {days.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Nessun dato di consumo ancora disponibile.</p>
      ) : (
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={44} />
              <Tooltip
                cursor={{ fill: "rgba(16,185,129,0.06)" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                formatter={(v) => [`${Number(v).toLocaleString("it-IT")} token`, "Consumo"]}
                labelFormatter={(l) => `Giorno ${l}`}
              />
              <Bar dataKey="tokens" radius={[4, 4, 0, 0]} maxBarSize={48}>
                {days.map((d, i) => (
                  <Cell key={i} fill={d.tokens >= maxTokens && maxTokens > 1 ? "#0f766e" : "#10b981"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Sync ogni {interval} min · tetto giornaliero impostato: <strong>{dailyBudget.toLocaleString("it-IT")}</strong> token. Il budget dell'account Pipedrive è a token e condiviso: la sync si ferma al tetto e riprende il giorno dopo, lasciando margine al tuo lavoro manuale.
      </p>
    </div>
  );
}

function SalesTrailCard() {
  const qc = useQueryClient();
  const { data: st, isLoading } = useQuery({
    queryKey: ["salestrail-status"],
    queryFn: async () => (await api.get("/integrations/salestrail/status")).data,
    refetchInterval: (query) => (query.state.data?.sync?.state === "running" ? 3000 : false),
  });
  const running = st?.sync?.state === "running";
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }) : "Mai");

  const sync = useMutation({
    mutationFn: async () => (await api.post("/integrations/salestrail/sync")).data,
    onSuccess: (r) => {
      if (r.started) toast.success("Sincronizzazione SalesTrail avviata");
      else toast.info(r.message || "Sincronizzazione già in corso");
      qc.invalidateQueries({ queryKey: ["salestrail-status"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sincronizzazione fallita"),
  });

  return (
    <Card className="shadow-sm" data-testid="salestrail-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <PhoneCall className="h-5 w-5 text-indigo-600" /> SalesTrail (registro chiamate)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                {st?.connected ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <XCircle className="h-6 w-6 text-slate-400" />}
                <div>
                  <p className="font-medium" data-testid="salestrail-connection-status">
                    {st?.connected ? "Collegato" : st?.configured ? "Configurato — sincronizza per iniziare" : "Non configurato"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Incrocia le chiamate ai lead per numero di telefono.
                  </p>
                </div>
              </div>
            </div>

            {!st?.configured && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Credenziali SalesTrail (SALESTRAIL_USER / SALESTRAIL_PASS) non configurate sul server.</span>
              </div>
            )}

            {st?.configured && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Stat icon={PhoneCall} label="Chiamate scaricate" value={st?.total_calls ?? 0} />
                  <Stat icon={Users} label="Lead chiamati" value={st?.leads_called ?? 0} />
                  <Stat icon={Clock} label="Ultima sincronizzazione" value={fmt(st?.sync?.finished_at)} />
                </div>

                {running && (
                  <div className="flex items-center gap-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800" data-testid="salestrail-sync-progress">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sincronizzazione chiamate in corso…</span>
                  </div>
                )}

                {st?.sync?.state === "error" && st?.sync?.error && (
                  <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="salestrail-error">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{st.sync.error}</span>
                  </div>
                )}

                <Button className="w-full gap-2 sm:w-auto" data-testid="salestrail-sync-button" onClick={() => sync.mutate()} disabled={running || sync.isPending}>
                  <RefreshCw className={`h-4 w-4 ${running || sync.isPending ? "animate-spin" : ""}`} /> Sincronizza chiamate
                </Button>
                <p className="text-xs text-muted-foreground">
                  Aggiornamento automatico ogni 5 minuti. Nella tabella Lead vedi la colonna "Chiamato?" e il filtro "Mai chiamati".
                </p>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MetaCard() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ access_token: "", ad_account_id: "", app_id: "", app_secret: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const { data: meta, isLoading } = useQuery({
    queryKey: ["meta-status"],
    queryFn: async () => (await api.get("/integrations/meta/status")).data,
  });

  const connect = useMutation({
    mutationFn: async () => (await api.post("/integrations/meta/connect", {
      access_token: form.access_token.trim(),
      ad_account_id: form.ad_account_id.trim(),
      app_id: form.app_id.trim() || null,
      app_secret: form.app_secret.trim() || null,
    })).data,
    onSuccess: (r) => {
      toast.success(`Meta Ads collegato: ${r.account_name}${r.long_lived ? " (token a lunga durata ~60 giorni)" : ""}`);
      setForm({ access_token: "", ad_account_id: "", app_id: "", app_secret: "" });
      qc.invalidateQueries({ queryKey: ["meta-status"] });
      qc.invalidateQueries({ queryKey: ["analytics-campaigns"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Collegamento Meta fallito"),
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.post("/integrations/meta/disconnect")).data,
    onSuccess: () => {
      toast.success("Meta Ads scollegato");
      qc.invalidateQueries({ queryKey: ["meta-status"] });
      qc.invalidateQueries({ queryKey: ["analytics-campaigns"] });
    },
  });

  const connected = meta?.connected;

  return (
    <Card className="shadow-sm" data-testid="meta-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Megaphone className="h-5 w-5 text-blue-600" /> Meta Ads (sola lettura spesa)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : connected ? (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                <div>
                  <p className="font-medium" data-testid="meta-connection-status">Collegato</p>
                  <p className="text-sm text-muted-foreground">
                    {meta.account_name} ({meta.account_id}) · {meta.user_name} · {meta.currency}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="gap-2" data-testid="disconnect-meta-button" onClick={() => disconnect.mutate()}>
                <Unplug className="h-4 w-4" /> Scollega
              </Button>
            </div>
            {meta.token_valid === false && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="meta-token-expired">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Token Meta scaduto o non valido</p>
                  <p className="text-sm text-amber-700">
                    Il collegamento risulta attivo ma Meta rifiuta le richieste, perciò i costi non vengono caricati.
                    Genera un nuovo Access Token e ricollega: <strong>Scollega</strong> e poi inserisci di nuovo token e ID account qui sotto.
                    Per un token a lunga durata (~60 giorni) aggiungi anche App ID e App Secret.
                  </p>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              La spesa delle campagne è visibile nella pagina <strong>Campagne</strong>: margine, ROAS e classifiche.
              {!meta.long_lived && " Nota: il token inserito potrebbe scadere in poche ore; per un token da ~60 giorni inserisci anche App ID e App Secret al prossimo collegamento."}
            </p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 rounded-lg border border-border p-4">
              <XCircle className="h-6 w-6 text-slate-400" />
              <div>
                <p className="font-medium" data-testid="meta-connection-status">Non collegato</p>
                <p className="text-sm text-muted-foreground">
                  Serve un Access Token con permesso <code>ads_read</code> e l'ID account (act_...).
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">Access Token *</label>
                <Input data-testid="meta-token-input" value={form.access_token} onChange={set("access_token")} placeholder="EAAB..." />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">ID account pubblicitario *</label>
                <Input data-testid="meta-account-input" value={form.ad_account_id} onChange={set("ad_account_id")} placeholder="act_1234567890" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">App ID (opzionale, per token ~60gg)</label>
                <Input data-testid="meta-appid-input" value={form.app_id} onChange={set("app_id")} placeholder="123456789" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-muted-foreground">App Secret (opzionale, per token ~60gg)</label>
                <Input data-testid="meta-appsecret-input" type="password" value={form.app_secret} onChange={set("app_secret")} placeholder="••••••••" />
              </div>
            </div>
            <Button
              className="w-full gap-2 sm:w-auto"
              data-testid="connect-meta-button"
              onClick={() => connect.mutate()}
              disabled={!form.access_token.trim() || !form.ad_account_id.trim() || connect.isPending}
            >
              {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Collega Meta Ads
            </Button>
            <p className="text-xs text-muted-foreground">
              Token: da{" "}
              <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="underline">Graph API Explorer</a>{" "}
              (permesso ads_read). ID account: in Ads Manager, il numero dopo "act=" nell'URL.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="mt-1 truncate text-sm font-medium">{String(value)}</p>
    </div>
  );
}
